import React from 'react';

import H from '@hero/ui-kit/typography/H';
import Tag from '@hero/ui-kit/components/Tag';
import type { SignalsDTO } from '@hero/hero-types';

import { DetailsContainer, DetailLabel, Detail } from '../../../../components/Details';
import { getOutcomeLabel } from './labels';
import * as styles from './style.module.css';

type Props = {
    signals: SignalsDTO | null | undefined;
};

const DASH = '-';

const fmtNumber = (value: unknown, digits = 0): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return DASH;
    return digits > 0 ? value.toFixed(digits) : String(value);
};

const fmtInt = (value: unknown): string => fmtNumber(value, 0);

const fmtPct = (value: unknown, digits = 1): string => {
    const formatted = fmtNumber(value, digits);
    return formatted === DASH ? DASH : `${formatted}%`;
};

const fmtDate = (iso: unknown): string => {
    if (typeof iso !== 'string' || !iso) return DASH;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 10);
};

const fmtDateTime = (iso: unknown): string => {
    if (typeof iso !== 'string' || !iso) return DASH;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
};

// Inline warning chip rendered next to a value when a signal is out of range.
const Flag: React.FC<{ type: 'warn' | 'fail'; children: React.ReactNode }> = ({
    type,
    children
}) => (
    <Tag type={type} size="small">
        {children}
    </Tag>
);

const ValueWithFlag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className={styles.valueWithFlag}>{children}</span>
);

const SignalsBlock: React.FC<Props> = ({ signals }) => {
    if (!signals || typeof signals !== 'object') {
        return null;
    }

    const failureRate = signals.failure_rate_pct;
    const failureClass =
        typeof failureRate === 'number' && failureRate >= 15
            ? styles.tileValueFail
            : typeof failureRate === 'number' && failureRate >= 5
              ? styles.tileValueWarn
              : '';

    const popen = signals.popen_stats ?? null;
    const belowCrit = popen?.below_crit ?? 0;
    const outcomeEntries = signals.outcomes ? Object.entries(signals.outcomes) : [];
    const perSlotEntries = signals.per_slot_failures
        ? Object.entries(signals.per_slot_failures)
        : [];
    const shiftTest = signals.popen_shift_test;

    return (
        <div className={styles.section}>
            <H role="h5">Signals</H>

            {/* Headline metrics */}
            <div className={styles.tiles}>
                <div className={styles.tile}>
                    <span className={styles.eyebrow}>Dispense attempts</span>
                    <span className={styles.tileValue}>
                        {fmtInt(signals.total_dispense_attempts)}
                    </span>
                </div>
                <div className={styles.tile}>
                    <span className={styles.eyebrow}>Success rate</span>
                    <span className={`${styles.tileValue} ${styles.tileValueSuccess}`}>
                        {fmtPct(signals.success_rate_pct)}
                    </span>
                </div>
                <div className={styles.tile}>
                    <span className={styles.eyebrow}>Failure rate</span>
                    <span className={`${styles.tileValue} ${failureClass}`}>
                        {fmtPct(signals.failure_rate_pct)}
                    </span>
                </div>
            </div>

            <div className={styles.groupGrid}>
                {/* Overview / meta */}
                <div className={styles.subSection}>
                    <h4 className={styles.groupHeading}>Overview</h4>
                    <DetailsContainer>
                        <DetailLabel>External serial</DetailLabel>
                        <Detail>{signals.external_serial || DASH}</Detail>

                        <DetailLabel>Serial number</DetailLabel>
                        <Detail>{signals.serial_number || DASH}</Detail>

                        <DetailLabel>Sensor type</DetailLabel>
                        <Detail>{signals.sensor_type || DASH}</Detail>

                        <DetailLabel>Odometer</DetailLabel>
                        <Detail>{fmtInt(signals.device_meta?.odometer)}</Detail>

                        <DetailLabel>Manufactured</DetailLabel>
                        <Detail>{fmtDate(signals.device_meta?.manufactured)}</Detail>

                        <DetailLabel>Activated</DetailLabel>
                        <Detail>{fmtDate(signals.device_meta?.activated)}</Detail>

                        <DetailLabel>Row count</DetailLabel>
                        <Detail>{fmtInt(signals.row_count)}</Detail>

                        <DetailLabel>Coverage</DetailLabel>
                        <Detail>{fmtPct(signals.coverage_pct)}</Detail>
                    </DetailsContainer>
                </div>

                {/* Dispense outcomes */}
                <div className={styles.subSection}>
                    <h4 className={styles.groupHeading}>Dispense outcomes</h4>
                    <DetailsContainer>
                        <DetailLabel>Attempts</DetailLabel>
                        <Detail>{fmtInt(signals.total_dispense_attempts)}</Detail>

                        <DetailLabel>Success rate</DetailLabel>
                        <Detail>{fmtPct(signals.success_rate_pct, 2)}</Detail>

                        <DetailLabel>Failure rate</DetailLabel>
                        <Detail>{fmtPct(signals.failure_rate_pct, 2)}</Detail>

                        <DetailLabel>Partial seal picks</DetailLabel>
                        <Detail>{fmtInt(signals.partial_seal_picks)}</Detail>

                        <DetailLabel>Outcomes</DetailLabel>
                        <Detail>
                            {outcomeEntries.length === 0
                                ? DASH
                                : outcomeEntries.map(([key, count]) => (
                                      <div
                                          key={key}
                                      >{`${getOutcomeLabel(key)}: ${fmtInt(count)}`}</div>
                                  ))}
                        </Detail>

                        <DetailLabel>Per-slot failures</DetailLabel>
                        <Detail>
                            {perSlotEntries.length === 0
                                ? DASH
                                : perSlotEntries.map(([slot, stats]) => (
                                      <div key={slot}>
                                          {`${slot}: ${fmtInt(stats?.failures)}/${fmtInt(
                                              stats?.total
                                          )} (${fmtPct(stats?.pct)})`}
                                      </div>
                                  ))}
                        </Detail>
                    </DetailsContainer>
                </div>

                {/* Pressure */}
                <div className={styles.subSection}>
                    <h4 className={styles.groupHeading}>Pressure (popen)</h4>
                    <DetailsContainer>
                        <DetailLabel>Average</DetailLabel>
                        <Detail>{fmtNumber(popen?.avg, 1)}</Detail>

                        <DetailLabel>Min / Max</DetailLabel>
                        <Detail>{`${fmtNumber(popen?.min, 1)} / ${fmtNumber(popen?.max, 1)}`}</Detail>

                        <DetailLabel>Below warn</DetailLabel>
                        <Detail>{fmtInt(popen?.below_warn)}</Detail>

                        <DetailLabel>Below crit</DetailLabel>
                        <Detail>
                            <ValueWithFlag>
                                {fmtInt(popen?.below_crit)}
                                {belowCrit > 0 ? <Flag type="fail">Critical</Flag> : null}
                            </ValueWithFlag>
                        </Detail>

                        <DetailLabel>Shift test</DetailLabel>
                        <Detail>
                            {!shiftTest
                                ? 'Not run'
                                : shiftTest.passed === null
                                  ? 'Inconclusive'
                                  : shiftTest.passed
                                    ? 'Passed'
                                    : 'Failed'}
                        </Detail>
                    </DetailsContainer>
                </div>

                {/* Motor & mechanics */}
                <div className={styles.subSection}>
                    <h4 className={styles.groupHeading}>Motor &amp; mechanics</h4>
                    <DetailsContainer>
                        <DetailLabel>Motor errors</DetailLabel>
                        <Detail>{fmtInt(signals.motor_errors)}</Detail>

                        <DetailLabel>EM aborts</DetailLabel>
                        <Detail>{fmtInt(signals.em_abort_count)}</Detail>

                        <DetailLabel>Watchdog reboots</DetailLabel>
                        <Detail>{fmtInt(signals.watchdog_reboot_count)}</Detail>

                        <DetailLabel>Sticky aborts</DetailLabel>
                        <Detail>{fmtInt(signals.sticky_aborts)}</Detail>

                        <DetailLabel>Nib cleaning attempts</DetailLabel>
                        <Detail>{fmtInt(signals.nib_cleaning_attempts)}</Detail>

                        <DetailLabel>Boot count</DetailLabel>
                        <Detail>{fmtInt(signals.boot_count)}</Detail>
                    </DetailsContainer>
                </div>

                {/* Connectivity */}
                <div className={styles.subSection}>
                    <h4 className={styles.groupHeading}>Connectivity</h4>
                    <DetailsContainer>
                        <DetailLabel>Power failures</DetailLabel>
                        <Detail>{fmtInt(signals.power_fail_count)}</Detail>

                        <DetailLabel>First power fail</DetailLabel>
                        <Detail>{fmtDateTime(signals.first_power_fail)}</Detail>

                        <DetailLabel>Hero offline</DetailLabel>
                        <Detail>{fmtInt(signals.hero_offline_count)}</Detail>

                        <DetailLabel>First offline</DetailLabel>
                        <Detail>{fmtDateTime(signals.first_hero_offline)}</Detail>

                        <DetailLabel>Disconnections</DetailLabel>
                        <Detail>
                            <ValueWithFlag>
                                {fmtInt(signals.hero_disconnected_count)}
                                {signals.hero_disconnected_for_24h ? (
                                    <Flag type="fail">Offline &gt; 24h</Flag>
                                ) : null}
                            </ValueWithFlag>
                        </Detail>

                        <DetailLabel>Recovered</DetailLabel>
                        <Detail>
                            <ValueWithFlag>
                                {signals.device_recovered ? 'Yes' : 'No'}
                                {!signals.device_recovered ? (
                                    <Flag type="warn">Not recovered</Flag>
                                ) : null}
                            </ValueWithFlag>
                        </Detail>
                    </DetailsContainer>
                </div>
            </div>
        </div>
    );
};

export default SignalsBlock;
